import { useCallback } from 'react';
import produce from 'immer';
import { SearchResult, SearchResponse } from '@scale/shared/types/chat';
import AdditionalReferencesAccordion from './AdditionalReferencesAccordion';

type AdditionalReferencesSectionProps = {
  additionalReferences: SearchResponse[];
  setAdditionalReferences: (
    callback: (currentAdditionalReferences: SearchResponse[]) => SearchResponse[],
  ) => void;
  readOnly: boolean;
  searchModelId: string;
};

const AdditionalReferencesSection: React.FC<AdditionalReferencesSectionProps> = ({
  additionalReferences,
  setAdditionalReferences,
  readOnly,
  searchModelId,
}) => {
  const setAdditionalReference = useCallback(
    (
      referenceIndex: number,
      update: { searchQuery: string; searchQueryResults: SearchResult[] },
    ) => {
      setAdditionalReferences(prev =>
        produce(prev, draft => {
          if (update.searchQuery !== undefined) {
            draft[referenceIndex].searchTerm = update.searchQuery;
          }
          if (update.searchQueryResults !== undefined) {
            draft[referenceIndex].searchResults = update.searchQueryResults;
          }
        }),
      );
    },
    [setAdditionalReferences],
  );

  const deleteAdditionalReference = useCallback(
    (referenceIndex: number) => {
      setAdditionalReferences(prev => prev.filter((_, index) => index !== referenceIndex));
    },
    [setAdditionalReferences],
  );

  if (!additionalReferences.length) {
    return null;
  }

  return (
    <>
      <div className="mb-1 text-sm font-medium text-neutral-700">Additional References</div>
      <div className="flex flex-col gap-4">
        {additionalReferences.map((additionalReference, index) => (
          <div key={`${index}-${additionalReference.searchTerm}`}>
            <AdditionalReferencesAccordion
              setAdditionalReference={setAdditionalReference}
              deleteAdditionalReference={deleteAdditionalReference}
              referenceIndex={index}
              existingSearchQuery={additionalReference.searchTerm}
              existingSearchQueryResults={additionalReference.searchResults}
              readOnly={readOnly}
              searchModelId={searchModelId}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default AdditionalReferencesSection;
